import { useCallback, useState } from 'react';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import { Offcanvas } from 'react-bootstrap';
import { Flex, View, DialogContainer, Dialog, Content, Header } from '@adobe/react-spectrum';
import { FilterIcon } from '../../../../components/protected/Icons/IconsLib';
import { CardChecklist } from 'react-bootstrap-icons';
import { useDeviceDetection } from '../../../../hooks/UseDeviceDetection';
import UsersFilterComponent from '../../../../components/protected/layout-components/FilterComponent/UsersFilter/UsersFilterComponent';
import { useTranslation } from 'react-i18next';
import styles from './filter_component.module.css';
import StandardStatusFilterDropdown from './StandardStatusFilterDropdown';
import IssueTypeFilterComponent from './IssueTypeFilterComponent';

interface IFilterComponentProps {
  onFilterChange: (filterParams: {}) => void;
  filterParams: any;
  filterStyle?: any;
  issueTypes?: Array<{ id: string; name: string }>;
  statusList?: Array<{ ident: string; title: string; color: string }>;
  viewCounts?: Record<string, number>;
}

const FilterComponent: React.FC<IFilterComponentProps> = ({
  onFilterChange,
  filterParams,
  filterStyle,
  issueTypes = [],
  statusList = [],
  viewCounts = {},
}) => {
  const { isMobile } = useViewInfo();
  const { t } = useTranslation('issue');
  const _t = (key: string) => t(`issue_filters.${key}`, { ns: 'issue' });
  const deviceInfo = useDeviceDetection(isMobile);
  const [show, setShow] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedView, setSelectedView] = useState<string>(filterParams?.FILTER.COL.find((col: any) => col.name === 'view')?.value || 'issues');

  const createParams = useCallback((filterTypeName: any, filterValue: any) => {
    const updateFilterParams = (name: string, value: any) => {
      let updatedArray = filterParams.FILTER.COL.map((obj: any) =>
        obj.name === name ? { ...obj, value } : obj
      );
      const hasNameSearch = filterParams.FILTER.COL.some((obj: any) => obj.name === name);

      if (!hasNameSearch) {
        updatedArray.push({ name, value });
      }
      filterParams.FILTER.COL = updatedArray.filter((col: any) => col.value !== '');
    };

    switch (filterTypeName) {
      case 'view':
        updateFilterParams('view', filterValue);
        break;
      case 'customState':
        updateFilterParams('customState', filterValue);
        break;
      case 'user':
        updateFilterParams('user', filterValue);
        break;
      case 'standardStatus':
        updateFilterParams('standardStatus', Array.from(filterValue)[0] || '0');
        break;
      default:
        break;
    }
    onFilterChange(filterParams);
  }, [filterParams, onFilterChange]);

  const shouldUseMobileLayout = deviceInfo.shouldUseMobileLayout();
  const isMobileOrSmallScreen = shouldUseMobileLayout || deviceInfo.isIPad || deviceInfo.isTablet;
  const renderViewText = (label: string, viewKey: string) => (
    <Flex direction="row" flex={1} alignItems="center" justifyContent="space-between" gap="8px">
      <Flex direction="row" flex={1} alignItems="center" justifyContent="start" gap="8px">
        <View UNSAFE_className={selectedView === viewKey ? styles.filter_view_icon_selected : styles.filter_view_icon}>
          <CardChecklist size={15} />
        </View>
        <Flex position="relative" flex={1} UNSAFE_className={styles.filter_views_text}>
          {label}
        </Flex>
      </Flex>
      <View UNSAFE_className={selectedView === viewKey ? styles.filter_view_badge_selected : styles.filter_view_badge}>
        {viewCounts[viewKey] ?? 0}
      </View>
    </Flex>
  );

  return isMobileOrSmallScreen ? (
    <View position="relative" width="100%" height="64px" UNSAFE_className={styles.mobile_header_parent}>
      <Flex direction="column" position="absolute" top="calc(50% - 16px)" left="50px" alignItems="start" justifyContent="start">
        <View position="relative" UNSAFE_className={styles.mobile_header_text_1}>
          {_t('header_text')}
        </View>
      </Flex>
      <View position="absolute" top="calc(50% - 12px)" left="16px" width="24px" height="24px" overflow="hidden">
        <span onClick={() => setShow(true)} className="me-2">
          <i className="bi bi-list"></i>
        </span>
        <Offcanvas show={show} onHide={() => setShow(false)} placement="start">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title></Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Flex
              direction="column"
              position="absolute"
              top="50px"
              left="15px"
              width="240px"
              alignItems="start"
              justifyContent="start"
              gap="5px"
              UNSAFE_className={styles.views_menu_container}
            >
              <Flex direction="row" alignSelf="stretch" alignItems="center" justifyContent="start" UNSAFE_className={styles.views_menu_heading}>
                <View position="relative" UNSAFE_className={styles.menu_heading_text}>
                  Views
                </View>
              </Flex>
              <Flex direction="column" alignSelf="stretch" alignItems="start" justifyContent="start" gap="4px" UNSAFE_className={styles.filter_views_parent}>
                <button
                  type="button"
                  style={{ width: '100%', background: 'transparent', border: 'none', padding: 0, textAlign: 'left' }}
                  onClick={() => {
                    setSelectedView('issues');
                    createParams('view', 'issues');
                  }}
                >
                  <Flex
                    direction="row"
                    height="36px"
                    alignItems="center"
                    justifyContent="center"
                    UNSAFE_className={selectedView === 'issues' ? styles.filter_views_container_selected : styles.filter_views_container}
                  >
                    {renderViewText(_t('requests_to_solve'), 'issues')}
                  </Flex>
                </button>
                <button
                  type="button"
                  style={{ width: '100%', background: 'transparent', border: 'none', padding: 0, textAlign: 'left' }}
                  onClick={() => {
                    setSelectedView('my_issues');
                    createParams('view', 'my_issues');
                  }}
                >
                  <Flex
                    direction="row"
                    height="36px"
                    alignItems="center"
                    justifyContent="center"
                    UNSAFE_className={selectedView === 'my_issues' ? styles.filter_views_container_selected : styles.filter_views_container}
                  >
                    {renderViewText(_t('my_requests'), 'my_issues')}
                  </Flex>
                </button>
              </Flex>
            </Flex>
          </Offcanvas.Body>
        </Offcanvas>
      </View>

      <View position="absolute" top="calc(50% - 12px)" left="calc(100% - 40px)" width="24px" height="24px" overflow="hidden">
        <div onClick={() => setIsOpen(true)}>
          <FilterIcon size={20} />
        </div>

        <DialogContainer isDismissable onDismiss={() => setIsOpen(false)}>
          {isOpen && (
            <Dialog size="L">
              <Content>
                <Header>
                  <Flex position="relative" direction="row" justifyContent="center" alignContent="center" width="100%">
                    <View position="relative" UNSAFE_className={styles.popup_header_text}>
                      {t('filters', { ns: 'layout_components' })}
                    </View>
                  </Flex>
                </Header>
                <View width="100%" position="relative" height="100vh">
                  <Flex
                    direction="column"
                    position="absolute"
                    width="95%"
                    top="30px"
                    left="16px"
                    alignItems="start"
                    justifyContent="start"
                    gap="24px"
                    UNSAFE_className={styles.popup_header_body_parent}
                  >
                    <Flex direction="column" width="100%" alignItems="start" justifyContent="start" gap="12px" UNSAFE_className={styles.popup_header_body}>
                      <StandardStatusFilterDropdown createParams={createParams} filterParams={filterParams} statusList={statusList} />
                      <UsersFilterComponent createParams={createParams} filterParams={filterParams} />
                      <IssueTypeFilterComponent createParams={createParams} filterParams={filterParams} issueTypes={issueTypes} />
                    </Flex>
                  </Flex>
                </View>
              </Content>
            </Dialog>
          )}
        </DialogContainer>
      </View>
    </View>
  ) : (
    <View width="100%" position="relative" backgroundColor="static-white" UNSAFE_className={styles.filter_menu_parent} UNSAFE_style={filterStyle}>
      <Flex direction="column" position="absolute" top="24px" left="0px" width="240px" alignItems="start" justifyContent="start" gap="5px" UNSAFE_className={styles.views_menu_container}>
        <Flex direction="column" alignSelf="stretch" alignItems="start" justifyContent="start" gap="4px" UNSAFE_className={styles.filter_views_parent}>
          <button
            type="button"
            style={{ width: '100%', background: 'transparent', border: 'none', padding: 0, textAlign: 'left' }}
            onClick={() => {
              setSelectedView('issues');
              createParams('view', 'issues');
            }}
          >
            <Flex
              direction="row"
              height="36px"
              alignItems="center"
              justifyContent="center"
              UNSAFE_className={selectedView === 'issues' ? styles.filter_views_container_selected : styles.filter_views_container}
            >
              {renderViewText(_t('requests_to_solve'), 'issues')}
            </Flex>
          </button>
          <button
            type="button"
            style={{ width: '100%', background: 'transparent', border: 'none', padding: 0, textAlign: 'left' }}
            onClick={() => {
              setSelectedView('my_issues');
              createParams('view', 'my_issues');
            }}
          >
            <Flex
              direction="row"
              height="36px"
              alignItems="center"
              justifyContent="center"
              UNSAFE_className={selectedView === 'my_issues' ? styles.filter_views_container_selected : styles.filter_views_container}
            >
              {renderViewText(_t('my_requests'), 'my_issues')}
            </Flex>
          </button>
        </Flex>
      </Flex>
      <View position="relative" top="110px" width="100%" height="1px" UNSAFE_className={styles.separator}></View>
      <Flex
        direction="column"
        position="absolute"
        top="125px"
        left="0px"
        width="240px"
        alignItems="start"
        justifyContent="start"
        gap="5px"
        UNSAFE_className={styles.filters_menu_container}
      >
        <Flex direction="row" alignSelf="stretch" alignItems="start" justifyContent="start" UNSAFE_className={styles.filters_menu_wrapper}>
          <View position="relative" UNSAFE_className={styles.menu_heading_text}>
            {t('filters', { ns: 'layout_components' })}
          </View>
        </Flex>
        <StandardStatusFilterDropdown createParams={createParams} filterParams={filterParams} statusList={statusList} />
        <UsersFilterComponent createParams={createParams} filterParams={filterParams} />
        <IssueTypeFilterComponent createParams={createParams} filterParams={filterParams} issueTypes={issueTypes} />
      </Flex>
    </View>
  );
};

export default FilterComponent;
